import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "../css/style.css";
import logo from "../assets/image/rccglogo.webp";

function VerifyEmail() {
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const inputRefs = useRef([]);
  const navigate = useNavigate();
  const isMounted = useRef(true);
  const TEST_BASE_URL = process.env.REACT_APP_BASE_URL;

  useEffect(() => {
    isMounted.current = true;
    const storedEmail = localStorage.getItem("userEmail");
    if (!storedEmail) {
      navigate("/signup");
      return;
    }
    setEmail(storedEmail);

    return () => {
      isMounted.current = false;
    };
  }, [navigate]);

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  async function handleVerify() {
    const otpCode = otp.join("");
    if (otpCode.length !== 6) {
      toast.error("Please enter all 6 digits", { theme: "colored" });
      return;
    }

    setLoading(true);
    try {
      const tempData = localStorage.getItem("tempUserData");
      if (!tempData) {
        toast.error("Session expired. Please sign up again.", {
          theme: "colored",
        });
        navigate("/signup");
        return;
      }

      const payload = {
        email: email,
        otp: otpCode,
      };

      const response = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        const apiUrl = `${TEST_BASE_URL}/auth/verify-registration-otp`;

        xhr.open("POST", apiUrl, true);
        xhr.setRequestHeader("Content-Type", "application/json");
        xhr.setRequestHeader("Accept", "application/json");

        xhr.onreadystatechange = function () {
          if (xhr.readyState === 4) {
            resolve({
              ok: xhr.status >= 200 && xhr.status < 300,
              status: xhr.status,
              responseText: xhr.responseText,
            });
          }
        };

        xhr.onerror = function () {
          reject(new Error("Network request failed"));
        };

        try {
          xhr.send(JSON.stringify(payload));
        } catch (err) {
          reject(err);
        }
      });

      let data;
      try {
        data = JSON.parse(response.responseText);
      } catch (err) {
        toast.error("Invalid response from server", { theme: "colored" });
        return;
      }

      if (!response.ok) {
        if (data.errors) {
          const errorMsg =
            data.errors.otp?.join(" ") ||
            data.errors.email?.join(" ") ||
            JSON.stringify(data.errors);
          toast.error(errorMsg, { theme: "colored" });
        } else if (data.message) {
          toast.error(data.message, { theme: "colored" });
        } else {
          toast.error("Verification failed. Please try again.", {
            theme: "colored",
          });
        }
        return;
      }

      toast.success("Email verified successfully!", { theme: "colored" });

      if (data.data?.token) {
        localStorage.setItem("authToken", data.data.token);
      }

      if (data.data?.user) {
        localStorage.setItem("user", JSON.stringify(data.data.user));
        localStorage.setItem(
          "userName",
          data.data.user.name || data.data.user.email
        );
        window.dispatchEvent(new Event("userUpdated"));
      }

      localStorage.removeItem("tempUserData");
      localStorage.removeItem("userEmail");

      setTimeout(() => navigate("/"), 1500);
    } catch (err) {
      console.error("Verification error:", err);
      toast.error("Network error. Please try again.", { theme: "colored" });
    } finally {
      setLoading(false);
    }
  }

  async function handleResendOtp() {
    if (resendTimer > 0) return;

    setResendLoading(true);
    try {
      const payload = { email: email };

      const response = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        const apiUrl = `${TEST_BASE_URL}/auth/send-otp`;

        xhr.open("POST", apiUrl, true);
        xhr.setRequestHeader("Content-Type", "application/json");
        xhr.setRequestHeader("Accept", "application/json");
        xhr.onreadystatechange = function () {
          if (xhr.readyState === 4) {
            resolve({
              ok: xhr.status >= 200 && xhr.status < 300,
              status: xhr.status,
              responseText: xhr.responseText,
            });
          }
        };

        xhr.onerror = function () {
          reject(new Error("Network request failed"));
        };

        try {
          xhr.send(JSON.stringify(payload));
        } catch (err) {
          reject(err);
        }
      });

      let data;
      try {
        data = JSON.parse(response.responseText);
      } catch (err) {
        toast.error("Invalid response from server", { theme: "colored" });
        return;
      }

      if (!response.ok) {
        if (data.message) {
          toast.error(data.message, { theme: "colored" });
        } else {
          toast.error("Failed to resend OTP", { theme: "colored" });
        }
        return;
      }

      toast.success("OTP resent successfully!", { theme: "colored" });
      setOtp(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();

      setResendTimer(30);
      const interval = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      console.error("Resend error:", err);
      toast.error("Network error. Please try again.", { theme: "colored" });
    } finally {
      setResendLoading(false);
    }
  }

  return (
    <section className="login">
      <div className="containerfluid">
        <div className="row loginrow">
          <div className="col-md-6">
            <div className="loginbg">
              <img src={logo} alt="RCCG Logo" />
              <h5 className="py-5">
                Welcome to RCCG New Beginning Chapel, Bournemouth, UK
              </h5>
              <p className="text-white">
                RCCG Bournemouth Membership Portal has been built to create a
                community where the church members can interact with each other
                seamlessly.
              </p>
            </div>
          </div>

          <div className="col-md-6">
            <div className="card resetcard">
              <h5>Verify Email</h5>
              <p>
                We have sent a 6-digit code to <b>{email || "your email"}</b>.
                Please enter it below to verify your email.
              </p>

              <div
                className="otp-inputs"
                style={{
                  display: "flex",
                  gap: "8px",
                }}
              >
                {otp.map((digit, i) => (
                  <input
                    key={i}
                    type="tel"
                    maxLength="1"
                    ref={(el) => (inputRefs.current[i] = el)}
                    value={digit}
                    onChange={(e) => handleOtpChange(i, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(i, e)}
                    className="otp-box"
                    inputMode="numeric"
                  />
                ))}
              </div>

              <div>
                <p>
                  Didn't receive a code?{" "}
                  <button
                    type="button"
                    onClick={handleResendOtp}
                    disabled={resendLoading || resendTimer > 0}
                    style={{
                      background: "none",
                      border: "none",
                      color: resendTimer > 0 ? "#999" : "#0066cc",
                      cursor: resendTimer > 0 ? "not-allowed" : "pointer",
                      textDecoration: "underline",
                      fontSize: "14px",
                    }}
                  >
                    <u>
                      {resendTimer > 0
                        ? `Resend in ${resendTimer}s`
                        : "Click to resend"}
                    </u>
                  </button>
                </p>
              </div>

              <button
                className="btn verify-btn text-white"
                disabled={loading}
                onClick={handleVerify}
              >
                {loading ? "Verifying..." : "Verify Email"}
              </button>

              <p>
                Not the right email?{" "}
                <a href="/signup" className="text-primary">
                  <u>Sign up again</u>
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default VerifyEmail;
